import React, { useState } from 'react';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import api from '../../services/api';

const UploadInventoryModal = ({ show, handleClose, onSuccess, inventory_file_updated_at }) => {
    const { projectId } = useParams();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            // Validate file type
            if (!selectedFile.name.match(/\.(csv|xlsx|xls)$/)) {
                toast.error('Please upload a valid CSV or Excel file');
                return;
            }
            // Validate file size (10MB = 10 * 1024 * 1024 bytes)
            if (selectedFile.size > 10 * 1024 * 1024) {
                toast.error('File size should not exceed 10MB');
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file first');
            return;
        }

        if (!projectId) {
            toast.error('Project ID is missing');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        const fileBlob = new Blob([file], { type: 'file/csv' });
        formData.append('inventory_file', fileBlob, file.name);
        formData.append('project_id', projectId);

        try {
            const response = await api.post('projects/inventory/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json'
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(progress);
                },
            });

            if (response.data.status) {
                toast.success(response.data.message || 'Inventory uploaded successfully');
                onSuccess?.();
                handleClose();
            } else {
                throw new Error(response.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload inventory');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setFile(null);
        }
    };

    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            centered 
            size="lg" 
            style={{ 
                maxWidth: '50%', 
                margin: 'auto',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>Upload Inventory</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <p className="text-muted mb-3">
                        Upload your daily inventory data using a CSV or Excel file.
                    </p>
                    <div className="d-flex align-items-center gap-3">
                        <input
                            type="file"
                            className="form-control"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileChange}
                            disabled={uploading}
                        />
                        {file && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setFile(null)}
                                disabled={uploading}
                            >
                                <Icon icon="mdi:close" />
                            </Button>
                        )}
                    </div>
                </div>

                {uploading && (
                    <div className="mb-3">
                        <ProgressBar
                            now={uploadProgress}
                            label={`${uploadProgress}%`}
                            animated
                        />
                    </div>
                )}

                <div className="alert alert-info">
                    <h6 className="mb-2">
                        <Icon icon="mdi:information" className="me-2" />
                        File Requirements
                    </h6>
                    <ul className="mb-0">
                        <li>Supported formats: CSV, XLSX, XLS</li>
                        <li>Maximum file size: 10MB</li>
                        <li>Note: Uploading a new file will replace any existing inventory file for this project</li>
                    </ul>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex align-items-center gap-3">
                    {inventory_file_updated_at && (
                        <small className="text-muted">
                            Last updated: {formatDate(inventory_file_updated_at)}
                        </small>
                    )}
                    <Button variant="secondary" onClick={handleClose} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={!file || uploading}
                    >
                        {uploading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Icon icon="mdi:upload" className="me-2" />
                                Upload
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default UploadInventoryModal; 